import { OnInit, ChangeDetectionStrategy, Component, ViewEncapsulation, ElementRef, computed } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IInsertMediaResult, IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { AccessRightType, ApplicationSettingsType, FileType } from '@fi-shared/types';
import { fiHasAccessToApplicationType } from '@fi-shared/utils';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { Observable, Subject } from 'rxjs';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { FiFileUploadHelperService } from '@fi-shared/fi-insert-media/fi-file-upload-helper.service';

interface IInsertMediaDialogData {
    filesToUpload: ArrayLike<File>;
    multiple: boolean;
    accept: string;
    allowStoreInMyFiles: boolean;
    allowedFileTypes: FileType[];
    contentLanguageId: number;

    // invoked when user chooses files from device,
    // return promise with false result for prevent uploading file
    onFilesSelecting: (files: ArrayLike<File>) => Promise<boolean>;

    // invoked when user selects some media bank files in list and clicks 'Insert' button
    // return promise with false result for prevent inserting selected media bank files
    onMediaBankFilesInserting: (attachments: IInsertMediaUploadItem[]) => Promise<boolean>;
}

@Component({
    selector: 'fi-insert-media-dialog',
    template: `
        <fi-insert-media
          #fiInsertMedia
          [attachments]="attachments"
          [wrapperElementRef]="elementRef"
          (attachmentsChange)="onAttachmentsChanged($event)"
          [filesToUpload]="data.filesToUpload"
          [multiple]="data.multiple"
          [accept]="data.accept"
          [allowedFileTypes]="data.allowedFileTypes"
          [allowStoreInMyFiles]="allowStoreInMyFiles()"
          [onFilesSelecting]="data.onFilesSelecting"
          (showInsertFileErrorTooltip)="onShowInsertFileErrorTooltip()"
          >
        </fi-insert-media>
        
        @if (isMobilePhone()) {
          <div class="dnd-description-container">
            <span
              class="dnd-description"
              (click)="fiInsertMedia.onBrowseDeviceInvoked()"
              [innerHTML]="'dnd_here_to_upload_mobile' | fiAppText"
            ></span>
          </div>
        }
        
        <div class="custom-footer-container" [class.mobile]="isMobilePhone()">
          @if (!isMobilePhone()) {
            <div class="dnd-description-container">
              <span class="dnd-description">{{ 'dnd_here_to_upload' | fiAppText }}</span>
              <span class="margin-text">{{ 'or' | fiAppText | lowercase }}</span>
              <button mat-flat-button color="primary" (click)="fiInsertMedia.onBrowseDeviceInvoked()">
                {{ 'ccmFileUpload.press_to_browse' | fiAppText }}
              </button>
              <fi-content-validator-tooltip
                [contentType]="FiTooltipContentType.InsertFile"
                [insertFileErrorContentText]="getInsertFileErrorContentText()"
                [showErrorBorder]="false"
                [showErrorTooltip$]="showInsertFileErrorTooltip$"
                >
              </fi-content-validator-tooltip>
            </div>
          }
        
          <div class="dialog-actions-container">
            <button mat-button (click)="onNoButtonClick()">{{ 'common.cancel' | fiAppText }}</button>
            <button
              mat-button
              class="BrandedBackgroundColor"
              [disabled]="isYesButtonDisabled()"
              (click)="onYesButtonClick($event)"
              >
              {{ 'insert' | fiAppText }}
            </button>
          </div>
        </div>
        `,
    styleUrls: ['./fi-insert-media-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-insert-media-dialog' },
    standalone: false,
})
export class FiInsertMediaDialogComponent
    extends FiDialogsContentBase<IInsertMediaDialogData, IInsertMediaResult>
    implements OnInit
{
    attachments: IInsertMediaUploadItem[] = [];
    private yesButtonDisabled = true;
    private mediaBankHelperService: MediaBankHelperService;
    private fiUserDataService: FiUserDataService;
    private fiFileUploadHelperService: FiFileUploadHelperService;
    elementRef: ElementRef;

    ngOnInit(): void {
        this.mediaBankHelperService = this.injector.get(MediaBankHelperService);
        this.fiUserDataService = this.injector.get(FiUserDataService);
        this.elementRef = this.injector.get(ElementRef);
        this.fiFileUploadHelperService = this.injector.get(FiFileUploadHelperService);
    }

    isYesButtonDisabled() {
        return this.yesButtonDisabled;
    }

    isMobilePhone(): boolean {
        return this.injector.get(DetectDevicesService).isMobilePhone();
    }

    onAttachmentsChanged($event: IInsertMediaUploadItem[]) {
        this.attachments = $event;
        this.yesButtonDisabled = !$event.length;
    }

    private _showInsertFileErrorTooltip$ = new Subject<void>();
    protected get showInsertFileErrorTooltip$(): Observable<void> {
        return this._showInsertFileErrorTooltip$.asObservable();
    }
    protected onShowInsertFileErrorTooltip() {
        this._showInsertFileErrorTooltip$.next();
    }

    onYesButtonClick($event: MouseEvent) {
        if (this.data.onMediaBankFilesInserting) {
            $event.preventDefault();
            this.data.onMediaBankFilesInserting(this.attachments).then((result) => {
                if (result) {
                    this.closeDialogWithResult();
                }
            });
        } else {
            this.closeDialogWithResult();
        }
    }

    private closeDialogWithResult() {
        this.dialogRef.close({
            value: this.getResult(),
            reason: FiDialogCloseReason.YesButton,
        });
    }

    onNoButtonClick() {
        this.dialogRef.close({ reason: FiDialogCloseReason.NoButton });
    }

    getResult(): IInsertMediaResult {
        const mediaBankFileReferences = this.attachments.map((item) => {
            return this.mediaBankHelperService.getMediaBankFileReferenceFromMediaFile(item.mediaBankFile, {
                isNewUpload: !!item.file,
                showInMediaBank: item.showInMediaBank,
                contentLanguageId: this.data.contentLanguageId,
            });
        });
        return {
            mediaBankFiles: this.attachments.map((item) => item.mediaBankFile),
            mediaBankFileReferences: mediaBankFileReferences,
        };
    }

    allowStoreInMyFiles() {
        if (
            !fiHasAccessToApplicationType(
                ApplicationSettingsType.MediaBankLocal,
                AccessRightType.Share,
                this.fiUserDataService.getUserData().ModuleAccessRights,
            )
        )
            return false; // https://chainformation.atlassian.net/browse/FI-6323
        return this.data.allowStoreInMyFiles;
    }

    protected getInsertFileErrorContentText = computed<string>(() => {
        return this.fiFileUploadHelperService.getInsertFileErrorContentText(this.data.allowedFileTypes);
    });

    protected readonly FiTooltipContentType = FiTooltipContentType;
}
